/**
 * Button Text Controls
 *
 * Gutenberg controls for customizing button text in Item cards.
 * Similar pattern to join-program-button for consistent UX.
 *
 * @example
 * <ButtonTextPanel
 *   buttonTexts={{
 *     join: attributes.buttonTextJoin,
 *     claim: attributes.buttonTextClaim,
 *     claimed: attributes.buttonTextClaimed
 *   }}
 *   onChangeButtonTexts={(texts) => setAttributes(texts)}
 *   availableButtons={['join', 'claim', 'claimed', 'signIn', 'automatic']}
 * />
 */

import { PanelBody, TextControl } from '@wordpress/components';
import { __ } from '@wordpress/i18n';

// ============================================================================
// Types
// ============================================================================

export type ButtonTextType =
	| 'join'           // Join Program button
	| 'signIn'         // Sign In to Claim button
	| 'claim'          // Claim button
	| 'claimed'        // Claimed status
	| 'automatic'      // Automatic tracking status
	| 'redeem'         // Redeem button
	| 'insufficientCredits' // Insufficient credits message
	| 'viewDetails'    // View Details button
	| 'current'        // Current Tier status
	| 'locationOnly';  // Location-only reward status

export interface ButtonTexts {
	join?: string;
	signIn?: string;
	claim?: string;
	claimed?: string;
	automatic?: string;
	redeem?: string;
	insufficientCredits?: string;
	viewDetails?: string;
	current?: string;
	locationOnly?: string;
}

interface ButtonTextPanelProps {
	buttonTexts: ButtonTexts;
	onChangeButtonTexts: (texts: Partial<ButtonTexts>) => void;
	availableButtons: ButtonTextType[];
	initialOpen?: boolean;
}

// ============================================================================
// Button Text Configurations
// ============================================================================

const BUTTON_CONFIG: Record<ButtonTextType, { label: string; help: string; default: string }> = {
	join: {
		label: __('Join Button Text', 'leat-crm'),
		help: __('Text shown on the button for non-enrolled users', 'leat-crm'),
		default: 'Join Program',
	},
	signIn: {
		label: __('Sign In Button Text', 'leat-crm'),
		help: __('Text shown on the button for guest users', 'leat-crm'),
		default: 'Sign In to Claim',
	},
	claim: {
		label: __('Claim Button Text', 'leat-crm'),
		help: __('Text shown on the button for claimable actions', 'leat-crm'),
		default: 'Claim',
	},
	claimed: {
		label: __('Claimed Status Text', 'leat-crm'),
		help: __('Text shown when an action has been claimed', 'leat-crm'),
		default: 'Claimed',
	},
	automatic: {
		label: __('Automatic Status Text', 'leat-crm'),
		help: __('Text shown for automatically tracked actions', 'leat-crm'),
		default: 'Automatic',
	},
	redeem: {
		label: __('Redeem Button Text', 'leat-crm'),
		help: __('Text shown on the redeem button', 'leat-crm'),
		default: 'Redeem',
	},
	insufficientCredits: {
		label: __('Insufficient Credits Message', 'leat-crm'),
		help: __('Message shown when user cannot afford a reward', 'leat-crm'),
		default: 'Insufficient credits',
	},
	viewDetails: {
		label: __('View Details Button Text', 'leat-crm'),
		help: __('Text shown on the view details button', 'leat-crm'),
		default: 'View Details',
	},
	current: {
		label: __('Current Tier Status Text', 'leat-crm'),
		help: __('Text shown on the current tier indicator', 'leat-crm'),
		default: 'Current Tier',
	},
	locationOnly: {
		label: __('Location Only Text', 'leat-crm'),
		help: __('Text shown on rewards that can only be claimed at a physical location', 'leat-crm'),
		default: 'Claimable on location only',
	},
};

// ============================================================================
// Button Text Panel
// ============================================================================

/**
 * Complete button text customization panel for InspectorControls
 */
export const ButtonTextPanel: React.FC<ButtonTextPanelProps> = ({
	buttonTexts,
	onChangeButtonTexts,
	availableButtons,
	initialOpen = false,
}) => {
	const handleChange = (key: ButtonTextType, value: string) => {
		onChangeButtonTexts({ [key]: value });
	};

	return (
		<PanelBody
			title={__('Button Text', 'leat-crm')}
			initialOpen={initialOpen}
		>
			{availableButtons.map((buttonType) => {
				const config = BUTTON_CONFIG[buttonType];
				const value = buttonTexts[buttonType] || config.default;

				return (
					<TextControl
						key={buttonType}
						label={config.label}
						value={value}
						onChange={(newValue) => handleChange(buttonType, newValue)}
						help={config.help}
						__nextHasNoMarginBottom
					/>
				);
			})}
		</PanelBody>
	);
};

// ============================================================================
// Exports
// ============================================================================

export default ButtonTextPanel;
